import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/ApiConfig';
import { formatCurrencyView } from '../utils/currencyUtils';
import {
  calculateSaldoOrcamentoDespesa,
  calculateSaldoPorPrograma,
  calculateSaldoPorAcao,
  calculateSaldoTotalComDespesasSemPrograma,
} from '../utils/calculateUtil';
import LoadingModal from './modals/LoadingModal';

function SaldoOrcamentario({ cnpj, anoBase, token, filtros = {} }) {
  const [orcamentos, setOrcamentos] = useState([]);
  const [despesas, setDespesas] = useState([]);
  const [redistribuicoes, setRedistribuicoes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('geral'); // 'geral', 'programa', 'acao'
  const [selectedPrograma, setSelectedPrograma] = useState(null);

  useEffect(() => {
    if (cnpj && anoBase && token) {
      fetchData();
    }
  }, [cnpj, anoBase, token]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Buscar orçamentos
      const orcamentosResponse = await axios.get(
        `${API_BASE_URL.ORCAMENTO}/all`,
        {
          params: { cnpjAssociacao: cnpj, ano: anoBase },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Buscar despesas
      const despesasResponse = await axios.get(`${API_BASE_URL.DESPESA}/all`, {
        params: { cnpjAssociacao: cnpj, ano: parseInt(anoBase) },
        headers: { Authorization: `Bearer ${token}` },
      });

      // Buscar redistribuições
      const redistribuicoesResponse = await axios.get(
        `${API_BASE_URL.REDISTRIBUICAO}/all`,
        {
          params: { cnpjAssociacao: cnpj, ano: parseInt(anoBase) },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setOrcamentos(
        Array.isArray(orcamentosResponse.data) ? orcamentosResponse.data : []
      );
      setDespesas(
        Array.isArray(despesasResponse.data) ? despesasResponse.data : []
      );
      setRedistribuicoes(
        Array.isArray(redistribuicoesResponse.data)
          ? redistribuicoesResponse.data
          : []
      );
    } catch (error) {
      console.error('Erro ao carregar dados para saldo:', error);
      setOrcamentos([]);
      setDespesas([]);
      setRedistribuicoes([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Função para calcular redistribuições por programa
  const calculateRedistribuicoesPorPrograma = () => {
    const redistribuicoesPorPrograma = {};

    redistribuicoes.forEach(r => {
      const key = `${r.idPrograma}-${r.idAcao}-${r.idElementoDespesa}-${r.idItem}`;

      if (!redistribuicoesPorPrograma[key]) {
        redistribuicoesPorPrograma[key] = {
          idPrograma: r.idPrograma,
          idAcao: r.idAcao,
          idElementoDespesa: r.idElementoDespesa,
          idItem: r.idItem,
          totalRedistribuido: 0,
        };
      }

      // Somar valores (negativos são débitos, positivos são créditos)
      redistribuicoesPorPrograma[key].totalRedistribuido +=
        parseFloat(r.valor) || 0;
    });

    return redistribuicoesPorPrograma;
  };

  // Função auxiliar para calcular redistribuições de um item específico
  const getRedistribuicoesDoItem = (
    idPrograma,
    idAcao,
    idSubAcao,
    idElementoDespesa,
    idItem
  ) => {
    return redistribuicoes
      .filter(
        r =>
          r.idPrograma === idPrograma &&
          r.idAcao === idAcao &&
          (idSubAcao
            ? r.idSubAcao === idSubAcao
            : !r.idSubAcao || r.idSubAcao === null) &&
          r.idElementoDespesa === idElementoDespesa &&
          r.idItem === idItem
      )
      .reduce((total, r) => total + (parseFloat(r.valor) || 0), 0);
  };

  // Função auxiliar para calcular redistribuições de uma ação inteira
  const getRedistribuicoesDaAcao = (idPrograma, idAcao) => {
    return redistribuicoes
      .filter(r => r.idPrograma === idPrograma && r.idAcao === idAcao)
      .reduce((total, r) => total + (parseFloat(r.valor) || 0), 0);
  };

  const redistribuicoesPorPrograma = calculateRedistribuicoesPorPrograma();

  const saldoGeral = calculateSaldoTotalComDespesasSemPrograma(
    orcamentos,
    despesas
  );
  const saldosPorPrograma = calculateSaldoPorPrograma(
    orcamentos,
    despesas,
    true
  );
  // Agrupa subações e itens para cada ação
  const saldosPorAcaoRaw = selectedPrograma
    ? calculateSaldoPorAcao(orcamentos, despesas, selectedPrograma, true)
    : [];

  // Função para agrupar subações e itens
  function detalharSubAcoesEItens(acao) {
    // Subações
    // Agrupamento de subações por idSubAcao
    const subAcoesMap = new Map();
    orcamentos.forEach(o => {
      if (
        o.idPrograma === acao.idPrograma &&
        o.idAcao === acao.idAcao &&
        o.idSubAcao
      ) {
        const key = o.idSubAcao;
        if (!subAcoesMap.has(key)) {
          subAcoesMap.set(key, {
            idSubAcao: o.idSubAcao,
            descricaoSubAcao: o.descricaoSubAcao,
            totalOrcado: 0,
            totalPago: 0,
            itens: [],
          });
        }
        const subAcao = subAcoesMap.get(key);
        subAcao.totalOrcado += parseFloat(o.valor) || 0;
      }
    });
    // Somar despesas por subação (apenas não anuladas)
    despesas.forEach(d => {
      if (
        d.idPrograma === acao.idPrograma &&
        d.idAcao === acao.idAcao &&
        d.idSubAcao &&
        d.dataPagamento &&
        !d.dataAnulacao
      ) {
        const key = d.idSubAcao;
        if (subAcoesMap.has(key)) {
          const subAcao = subAcoesMap.get(key);
          subAcao.totalPago += parseFloat(d.valor) || 0;
        }
      }
    });
    // Agrupar itens por subação
    orcamentos.forEach(o => {
      if (
        o.idPrograma === acao.idPrograma &&
        o.idAcao === acao.idAcao &&
        o.idSubAcao &&
        o.idItem
      ) {
        const key = o.idSubAcao;
        if (subAcoesMap.has(key)) {
          const subAcao = subAcoesMap.get(key);
          // Evitar duplicidade de itens
          if (!subAcao.itens.some(i => i.idItem === o.idItem)) {
            // Somar valores pagos do item (apenas não anuladas)
            const itemDespesas = despesas.filter(
              d =>
                d.idPrograma === acao.idPrograma &&
                d.idAcao === acao.idAcao &&
                d.idSubAcao === key &&
                d.idItem === o.idItem &&
                d.dataPagamento &&
                !d.dataAnulacao
            );

            // Calcular redistribuições para este item específico da sub-ação
            const itemRedistribuicoes = redistribuicoes.filter(
              r =>
                r.idPrograma === acao.idPrograma &&
                r.idAcao === acao.idAcao &&
                r.idSubAcao === key &&
                r.idItem === o.idItem
            );
            const totalRedistribuido = itemRedistribuicoes.reduce(
              (sum, r) => sum + (parseFloat(r.valor) || 0),
              0
            );

            const valorOriginal = parseFloat(o.valor) || 0;
            const totalPago = itemDespesas.reduce(
              (sum, d) => sum + (parseFloat(d.valor) || 0),
              0
            );
            subAcao.itens.push({
              idItem: o.idItem,
              descricaoItem: o.descricaoItem,
              idElementoDespesa: o.idElementoDespesa,
              valorOriginal, // Valor orçado original
              totalOrcado: valorOriginal, // Para compatibilidade com cálculos existentes
              totalPago,
            });
          }
        }
      }
    });
    // Recalcular totais das sub-ações com base nos valores originais dos itens
    subAcoesMap.forEach(subAcao => {
      subAcao.totalOrcado = subAcao.itens.reduce(
        (sum, item) => sum + (item.valorOriginal || 0),
        0
      );
    });

    let subAcoes = Array.from(subAcoesMap.values());
    subAcoes = subAcoes.sort((a, b) => {
      if (a.idSubAcao < b.idSubAcao) return -1;
      if (a.idSubAcao > b.idSubAcao) return 1;
      return 0;
    });

    // Itens diretamente da ação (idSubAcao nulo)
    const itensOrcAcao = orcamentos.filter(
      o =>
        o.idPrograma === acao.idPrograma &&
        o.idAcao === acao.idAcao &&
        (!o.idSubAcao || o.idSubAcao === null) &&
        o.idItem
    );
    const itens = itensOrcAcao.map(itemOrc => {
      const itemDespesas = despesas.filter(
        d =>
          d.idPrograma === acao.idPrograma &&
          d.idAcao === acao.idAcao &&
          (!d.idSubAcao || d.idSubAcao === null) &&
          d.idItem === itemOrc.idItem &&
          d.dataPagamento &&
          !d.dataAnulacao
      );

      // Calcular redistribuições para este item específico
      const itemRedistribuicoes = redistribuicoes.filter(
        r =>
          r.idPrograma === acao.idPrograma &&
          r.idAcao === acao.idAcao &&
          (!r.idSubAcao || r.idSubAcao === null) &&
          r.idItem === itemOrc.idItem
      );
      const totalRedistribuido = itemRedistribuicoes.reduce(
        (sum, r) => sum + (parseFloat(r.valor) || 0),
        0
      );

      const valorOriginal = parseFloat(itemOrc.valor) || 0;
      const totalPago = itemDespesas.reduce(
        (sum, d) => sum + (parseFloat(d.valor) || 0),
        0
      );
      return {
        idItem: itemOrc.idItem,
        descricaoItem: itemOrc.descricaoItem,
        idElementoDespesa: itemOrc.idElementoDespesa,
        valorOriginal, // Valor orçado original
        totalOrcado: valorOriginal, // Para compatibilidade com cálculos existentes
        totalPago,
      };
    });

    return { ...acao, subAcoes, itens };
  }

  const saldosPorAcao = saldosPorAcaoRaw.map(detalharSubAcoesEItens);

  const getStatusColor = percentual => {
    if (percentual <= 50) return 'success';
    if (percentual <= 80) return 'warning';
    return 'danger';
  };

  const getStatusText = percentual => {
    if (percentual <= 50) return 'Baixa execução';
    if (percentual <= 80) return 'Execução moderada';
    if (percentual <= 100) return 'Alta execução';
    return 'Execução excedida';
  };

  if (isLoading) {
    return <LoadingModal loading={true} />;
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            <i className="bi bi-calculator me-2"></i>
            Saldo Orçamentário
          </h5>
          <div className="btn-group" role="group" size="sm">
            <button
              className={`btn btn-outline-primary btn-sm ${
                viewMode === 'geral' ? 'active' : ''
              }`}
              onClick={() => setViewMode('geral')}
            >
              Geral
            </button>
            <button
              className={`btn btn-outline-primary btn-sm ${
                viewMode === 'programa' ? 'active' : ''
              }`}
              onClick={() => setViewMode('programa')}
            >
              Por Programa
            </button>
            <button
              className={`btn btn-outline-primary btn-sm ${
                viewMode === 'acao' ? 'active' : ''
              }`}
              onClick={() => setViewMode('acao')}
            >
              Por Ação
            </button>
          </div>
        </div>
      </div>

      <div className="card-body">
        {viewMode === 'geral' && (
          <>
            <div className="row">
              <div className="col-md-3">
                <div className="card bg-primary text-white">
                  <div className="card-body text-center">
                    <h6>Total Orçado</h6>
                    <h4>{formatCurrencyView(saldoGeral.totalOrcadoGeral)}</h4>
                    <small>{saldoGeral.quantidadeOrcamentos} itens</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-info text-white">
                  <div className="card-body text-center">
                    <h6>Total Pago</h6>
                    <h4>{formatCurrencyView(saldoGeral.totalPagoGeral)}</h4>
                    <small>
                      {saldoGeral.quantidadeDespesasPagasTotal} pagamentos
                    </small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div
                  className={`card bg-${
                    saldoGeral.saldoGeral >= 0 ? 'success' : 'danger'
                  } text-white`}
                >
                  <div className="card-body text-center">
                    <h6>Saldo</h6>
                    <h4>{formatCurrencyView(saldoGeral.saldoGeral)}</h4>
                    <small>
                      {saldoGeral.saldoGeral >= 0 ? 'Disponível' : 'Excedido'}
                    </small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div
                  className={`card bg-${getStatusColor(
                    saldoGeral.percentualExecutadoGeral
                  )} text-white`}
                >
                  <div className="card-body text-center">
                    <h6>% Executado</h6>
                    <h4>{saldoGeral.percentualExecutadoGeral.toFixed(1)}%</h4>
                    <small>
                      {getStatusText(saldoGeral.percentualExecutadoGeral)}
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {saldoGeral.totalDespesasSemPrograma > 0 && (
              <div className="row mt-3">
                <div className="col-12">
                  <div className="alert alert-warning">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="alert-heading mb-1">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          Despesas sem Programa
                        </h6>
                        <p className="mb-0">
                          Existem despesas pagas que não estão vinculadas a
                          nenhum programa orçamentário.
                        </p>
                      </div>
                      <div className="text-end">
                        <div className="fw-bold fs-5">
                          {formatCurrencyView(
                            saldoGeral.totalDespesasSemPrograma
                          )}
                        </div>
                        <small>
                          {saldoGeral.quantidadeDespesasSemPrograma} despesas
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {viewMode === 'programa' && (
          <div className="table-responsive">
            <table className="table table-hover table-sm">
              <thead className="table-secondary">
                <tr>
                  <th>Programa</th>
                  <th className="text-end">Orçado</th>
                  <th className="text-end">Pago</th>
                  <th className="text-end">Saldo</th>
                  <th className="text-center">% Exec.</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {saldosPorPrograma.map(programa => (
                  <tr key={programa.idPrograma}>
                    <td>{programa.descricaoPrograma}</td>
                    <td className="text-end">
                      {formatCurrencyView(programa.totalOrcado)}
                    </td>
                    <td className="text-end">
                      {formatCurrencyView(programa.totalPago)}
                    </td>
                    <td
                      className={`text-end fw-bold ${
                        programa.saldo >= 0 ? 'text-success' : 'text-danger'
                      }`}
                    >
                      {formatCurrencyView(programa.saldo)}
                    </td>
                    <td className="text-center">
                      <span
                        className={`badge bg-${getStatusColor(
                          programa.percentualExecutado
                        )}`}
                      >
                        {programa.percentualExecutado.toFixed(1)}%
                      </span>
                    </td>
                    <td className="text-center">
                      <small
                        className={`text-${getStatusColor(
                          programa.percentualExecutado
                        )}`}
                      >
                        {getStatusText(programa.percentualExecutado)}
                      </small>
                    </td>
                    <td className="text-center">
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => {
                          setSelectedPrograma(programa.idPrograma);
                          setViewMode('acao');
                        }}
                        title="Ver ações deste programa"
                      >
                        <i className="bi bi-eye"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'acao' && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6>
                Ações do Programa:{' '}
                {saldosPorAcao[0]?.descricaoPrograma || 'N/A'}
              </h6>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setViewMode('programa')}
              >
                <i className="bi bi-arrow-left me-1"></i>
                Voltar aos Programas
              </button>
            </div>
            <div className="table-responsive">
              <table className="table table-hover table-sm">
                <thead className="table-secondary">
                  <tr>
                    <th>Ação</th>
                    <th className="text-end">Orçado</th>
                    <th className="text-end">Redistribuído</th>
                    <th className="text-end">Atual</th>
                    <th className="text-end">Pago</th>
                    <th className="text-end">Saldo</th>
                    <th className="text-center">% Exec.</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {saldosPorAcao.map(acao => {
                    // Calcular redistribuições para toda a ação (incluindo sub-ações)
                    const redistribuicoesDaAcao = getRedistribuicoesDaAcao(
                      acao.idPrograma,
                      acao.idAcao
                    ); // Calcular valor atual (orçado + redistribuições)
                    const valorAtual = acao.totalOrcado + redistribuicoesDaAcao;

                    // Calcular novo saldo (atual - pago)
                    const novoSaldo = valorAtual - acao.totalPago;

                    // Calcular percentual executado baseado no valor atual
                    const percentualExecutado =
                      valorAtual === 0
                        ? 0
                        : (acao.totalPago / valorAtual) * 100;

                    return (
                      <React.Fragment key={`${acao.idPrograma}-${acao.idAcao}`}>
                        <tr className="table-success">
                          <td>
                            <span style={{ fontWeight: 'bold' }}>
                              {acao.descricaoAcao}
                            </span>
                          </td>
                          <td className="text-end">
                            {formatCurrencyView(acao.totalOrcado)}
                          </td>
                          <td
                            className={`text-end ${
                              redistribuicoesDaAcao >= 0
                                ? 'text-success'
                                : 'text-danger'
                            }`}
                          >
                            {formatCurrencyView(redistribuicoesDaAcao)}
                          </td>
                          <td className="text-end fw-bold">
                            {formatCurrencyView(valorAtual)}
                          </td>
                          <td className="text-end">
                            {formatCurrencyView(acao.totalPago)}
                          </td>
                          <td
                            className={`text-end fw-bold ${
                              novoSaldo >= 0 ? 'text-success' : 'text-danger'
                            }`}
                          >
                            {formatCurrencyView(novoSaldo)}
                          </td>
                          <td className="text-center">
                            <span
                              className={`badge bg-${getStatusColor(
                                percentualExecutado
                              )}`}
                            >
                              {percentualExecutado.toFixed(1)}%
                            </span>
                          </td>
                          <td className="text-center">
                            <small
                              className={`text-${getStatusColor(
                                percentualExecutado
                              )}`}
                            >
                              {getStatusText(percentualExecutado)}
                            </small>
                          </td>
                        </tr>
                        {/* Subações como linhas da tabela */}
                        {acao.subAcoes &&
                          acao.subAcoes.length > 0 &&
                          acao.subAcoes.map(sub => {
                            // Calcular redistribuições para esta subação específica
                            const redistribuicoesDaSubAcao = Object.values(
                              redistribuicoesPorPrograma
                            )
                              .filter(
                                r =>
                                  r.idPrograma === acao.idPrograma &&
                                  r.idAcao === acao.idAcao &&
                                  // Verificar se existe orçamento para esta sub-ação com os mesmos IDs da redistribuição
                                  orcamentos.some(
                                    o =>
                                      o.idPrograma === r.idPrograma &&
                                      o.idAcao === r.idAcao &&
                                      o.idSubAcao === sub.idSubAcao &&
                                      o.idElementoDespesa ===
                                        r.idElementoDespesa &&
                                      o.idItem === r.idItem
                                  )
                              )
                              .reduce(
                                (total, r) => total + r.totalRedistribuido,
                                0
                              );

                            const valorAtualSub =
                              sub.totalOrcado + redistribuicoesDaSubAcao;
                            const novoSaldoSub = valorAtualSub - sub.totalPago;
                            const percentualExecutadoSub =
                              valorAtualSub === 0
                                ? 0
                                : (sub.totalPago / valorAtualSub) * 100;

                            return [
                              <tr className="table-primary" key={sub.idSubAcao}>
                                <td className="ps-4">
                                  <span
                                    style={{
                                      fontWeight: 'bold',
                                      color: 'inherit',
                                      fontSize: '0.95em',
                                    }}
                                  >
                                    {sub.descricaoSubAcao}
                                  </span>
                                </td>
                                <td className="text-end">
                                  {formatCurrencyView(sub.totalOrcado)}
                                </td>
                                <td
                                  className={`text-end ${
                                    redistribuicoesDaSubAcao >= 0
                                      ? 'text-success'
                                      : 'text-danger'
                                  }`}
                                >
                                  {formatCurrencyView(redistribuicoesDaSubAcao)}
                                </td>
                                <td className="text-end fw-bold">
                                  {formatCurrencyView(valorAtualSub)}
                                </td>
                                <td className="text-end">
                                  {formatCurrencyView(sub.totalPago)}
                                </td>
                                <td
                                  className={`text-end fw-bold ${
                                    novoSaldoSub >= 0
                                      ? 'text-success'
                                      : 'text-danger'
                                  }`}
                                >
                                  {formatCurrencyView(novoSaldoSub)}
                                </td>
                                <td className="text-center">
                                  <span
                                    className={`badge bg-${getStatusColor(
                                      percentualExecutadoSub
                                    )}`}
                                  >
                                    {percentualExecutadoSub.toFixed(1)}%
                                  </span>
                                </td>
                                <td className="text-center">
                                  <small
                                    className={`text-${getStatusColor(
                                      percentualExecutadoSub
                                    )}`}
                                  >
                                    {getStatusText(percentualExecutadoSub)}
                                  </small>
                                </td>
                              </tr>,
                              ...(sub.itens && sub.itens.length > 0
                                ? sub.itens.map(item => {
                                    // Calcular redistribuições para este item
                                    const redistribuicoesDoItem =
                                      getRedistribuicoesDoItem(
                                        acao.idPrograma,
                                        acao.idAcao,
                                        sub.idSubAcao,
                                        item.idElementoDespesa,
                                        item.idItem
                                      );

                                    const valorAtualItem =
                                      item.totalOrcado + redistribuicoesDoItem;
                                    const novoSaldoItem =
                                      valorAtualItem - item.totalPago;
                                    const percentualExecutadoItem =
                                      valorAtualItem === 0
                                        ? 0
                                        : (item.totalPago / valorAtualItem) *
                                          100;

                                    return (
                                      <tr
                                        key={item.idItem}
                                        style={{ background: '#eef6f7' }}
                                      >
                                        <td className="ps-5">
                                          <span
                                            style={{
                                              fontWeight: 'normal',
                                              color: 'inherit',
                                              fontSize: '0.92em',
                                            }}
                                          >
                                            {item.descricaoItem}
                                          </span>
                                        </td>
                                        <td className="text-end">
                                          {formatCurrencyView(
                                            item.valorOriginal
                                          )}
                                        </td>
                                        <td
                                          className={`text-end ${
                                            redistribuicoesDoItem >= 0
                                              ? 'text-success'
                                              : 'text-danger'
                                          }`}
                                        >
                                          {formatCurrencyView(
                                            redistribuicoesDoItem
                                          )}
                                        </td>
                                        <td className="text-end fw-bold">
                                          {formatCurrencyView(valorAtualItem)}
                                        </td>
                                        <td className="text-end">
                                          {formatCurrencyView(item.totalPago)}
                                        </td>
                                        <td
                                          className={`text-end fw-bold ${
                                            novoSaldoItem >= 0
                                              ? 'text-success'
                                              : 'text-danger'
                                          }`}
                                        >
                                          {formatCurrencyView(novoSaldoItem)}
                                        </td>
                                        <td className="text-center">
                                          <span
                                            className={`badge bg-${getStatusColor(
                                              percentualExecutadoItem
                                            )}`}
                                          >
                                            {percentualExecutadoItem.toFixed(1)}
                                            %
                                          </span>
                                        </td>
                                        <td className="text-center">
                                          <small
                                            className={`text-${getStatusColor(
                                              percentualExecutadoItem
                                            )}`}
                                          >
                                            {getStatusText(
                                              percentualExecutadoItem
                                            )}
                                          </small>
                                        </td>
                                      </tr>
                                    );
                                  })
                                : []),
                            ];
                          })}
                        {/* Itens da ação principal como linhas */}
                        {acao.itens &&
                          acao.itens.length > 0 &&
                          acao.itens.map(item => {
                            // Calcular redistribuições para este item específico
                            const redistribuicoesDoItem =
                              getRedistribuicoesDoItem(
                                acao.idPrograma,
                                acao.idAcao,
                                null, // idSubAcao = null para itens diretos da ação
                                item.idElementoDespesa,
                                item.idItem
                              );

                            const valorAtualItem =
                              item.totalOrcado + redistribuicoesDoItem;
                            const novoSaldoItem =
                              valorAtualItem - item.totalPago;
                            const percentualExecutadoItem =
                              valorAtualItem === 0
                                ? 0
                                : (item.totalPago / valorAtualItem) * 100;

                            return (
                              <tr
                                key={item.idItem}
                                style={{ background: '#eef6f7' }}
                              >
                                <td className="ps-4">
                                  <span
                                    style={{
                                      fontWeight: 'normal',
                                      color: 'inherit',
                                      fontSize: '0.92em',
                                    }}
                                  >
                                    {item.descricaoItem}
                                  </span>
                                </td>
                                <td className="text-end">
                                  {formatCurrencyView(item.valorOriginal)}
                                </td>
                                <td
                                  className={`text-end ${
                                    redistribuicoesDoItem >= 0
                                      ? 'text-success'
                                      : 'text-danger'
                                  }`}
                                >
                                  {formatCurrencyView(redistribuicoesDoItem)}
                                </td>
                                <td className="text-end fw-bold">
                                  {formatCurrencyView(valorAtualItem)}
                                </td>
                                <td className="text-end">
                                  {formatCurrencyView(item.totalPago)}
                                </td>
                                <td
                                  className={`text-end fw-bold ${
                                    novoSaldoItem >= 0
                                      ? 'text-success'
                                      : 'text-danger'
                                  }`}
                                >
                                  {formatCurrencyView(novoSaldoItem)}
                                </td>
                                <td className="text-center">
                                  <span
                                    className={`badge bg-${getStatusColor(
                                      percentualExecutadoItem
                                    )}`}
                                  >
                                    {percentualExecutadoItem.toFixed(1)}%
                                  </span>
                                </td>
                                <td className="text-center">
                                  <small
                                    className={`text-${getStatusColor(
                                      percentualExecutadoItem
                                    )}`}
                                  >
                                    {getStatusText(percentualExecutadoItem)}
                                  </small>
                                </td>
                              </tr>
                            );
                          })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default SaldoOrcamentario;
